import { IProductFile, IProductV2Edit, IProductV2Simple, SystemProductConfig, SystemProductType } from "@vierkant-software/types__api";
import { FullSyncableServiceBase, SyncLoadedData } from "./sync.service";
import { Injectable, signal } from "@angular/core";
import { APIStates } from "src/services/app.service";
import { environment } from "src/environments/environment";

export enum ProductDetailLevel {
    default = 0,
    simple = 1,
    full = 127,
}

export interface IProductV2EditWithFileUris extends IProductV2Edit {
    productFilesInfo?: (IProductFile & { uri: string })[];
}

type ProductDetailType = {
    [ProductDetailLevel.default]: Partial<IProductV2Simple>;
    [ProductDetailLevel.simple]: IProductV2Simple;
    [ProductDetailLevel.full]: IProductV2EditWithFileUris;
};

@Injectable({ providedIn: "root" })
export class ProductSyncService extends FullSyncableServiceBase <ProductDetailType, ProductDetailLevel> {
    #paymentProductConfigurationLoad?: Promise<Record<SystemProductType, SystemProductConfig>>;
    #paymentProductConfiguration = signal<Record<SystemProductType, SystemProductConfig>>({} as Record<SystemProductType, SystemProductConfig>);
    // The configuration is per studio: drop it on login, studio switch and logout, like `SettingsService`.
    private configResetSubs = this.app.$APIState.subscribe(state => {
        switch(state.type) {
            case APIStates.loggedIn:
            case APIStates.externalStudio:
            case APIStates.loggedOut:
                this.#resetPaymentProductConfig();
        }
    });

    override getDefaultValue(): ProductDetailType[ProductDetailLevel] {
        return {} as ProductDetailType[ProductDetailLevel];
    }
    protected readonly syncName = "productSync";
    protected override get usesDeviceSession(): boolean {
        return environment.isCashRegister;
    }

    protected mapFiles(
        data: SyncLoadedData<ProductDetailType, ProductDetailLevel>[],
        files: string[]
    ): SyncLoadedData<ProductDetailType, ProductDetailLevel>[] {
        const tempFiles = [...files];

        for (const entry of data) {
            const filesCount = entry.data.productFilesInfo?.length ?? 0;
            const entryFiles = tempFiles.splice(0, filesCount);

            entry.data = {
                ...entry.data,
                productFilesInfo: entry.data.productFilesInfo?.map((fileInfo: IProductFile, index: number) => ({
                    ...fileInfo,
                    uri: entryFiles?.[index] ?? "",
                })) || []
            };
        }

        return data;
    }


    /**
     * Lazy, synchronous lookup - `undefined` until the configuration has arrived. Code that must not
     * see that gap (e.g. form validators) awaits `loadPaymentProductConfig()` first.
     */
    public getPaymentProductConfig(productType: SystemProductType): SystemProductConfig | undefined {
        if (this.#paymentProductConfigurationLoad === undefined)
          void this.loadPaymentProductConfig().catch(e => console.warn('[AppService] Failed to load payment product configuration:', e));
        return this.#paymentProductConfiguration()[productType];
      }

    /**
     * Loads the configuration once and resolves when the cache behind `getPaymentProductConfig` is
     * filled. A failed load is forgotten, so the next call retries instead of staying rejected.
     */
    public loadPaymentProductConfig(): Promise<Record<SystemProductType, SystemProductConfig>> {
        if (this.#paymentProductConfigurationLoad !== undefined)
          return this.#paymentProductConfigurationLoad;
        const load = this.app.api.ProductWorker.getPaymentProductConfiguration()
          .then(config => {
            // A reset (studio switch) while this was in flight - don't fill the cache with the old studio's map.
            if (this.#paymentProductConfigurationLoad === load)
              this.#paymentProductConfiguration.set(config);
            return config;
          })
          .catch(e => {
            if (this.#paymentProductConfigurationLoad === load)
              this.#paymentProductConfigurationLoad = undefined;
            throw e;
          });
        this.#paymentProductConfigurationLoad = load;
        return load;
      }

    #resetPaymentProductConfig(): void {
        this.#paymentProductConfigurationLoad = undefined;
        this.#paymentProductConfiguration.set({} as Record<SystemProductType, SystemProductConfig>);
      }
}
